/**
 * Co-parentage — Activités & Garde (port `vCpActivities` du proto l. 2073).
 *
 * Phase 1, incrément 14. Vue dédiée à la gestion des activités du
 * foyer co-parentage avec un calendrier visuel de la rotation de garde.
 *
 * Composition (fidèle au proto) :
 *   - Calendrier 14 jours (today + 13 jours), couleur par parent A/B,
 *     today entouré, dot si activité ce jour.
 *   - Légende parentA / parentB.
 *   - Liste « Activités à venir » triée par date, filtrée `>= today`,
 *     avec icône type, badge parent, bouton supprimer.
 *   - Boutons + Ajouter (petit en haut de la liste, large en CTA bas).
 *   - Modal de saisie réutilisant `Modal.ts` (clé `cp-activities:add`).
 *
 * Garde-fous CLAUDE.md §2.1 :
 *   - Tous les libellés via `t(key)`.
 *   - Couleurs A/B pilotées par le store CSS modulaire (pas de hex
 *     en dur dans la TS).
 */
import { html, type TemplateResult } from 'lit-html';
import { Icon, Icons, type IconName } from '../../ui/icons.js';
import styles from './ActivitiesView.module.css';
import { getLang, t } from '../../i18n/index.js';
import { addActivity, getCurrentHome, removeActivity } from '../../stores/homes.js';
import { getCustodyParentCode } from './custody.js';
import { closeModal, getModal, openModal } from '../../stores/ui.js';
import { modalTemplate } from '../../ui/Modal.js';
import { dateToIso, formatIsoShort, todayIso } from '../../lib/date.js';
import type { CoparentingHomeData, CpActivity, CustodyConfig } from '@nestor/shared-types';
import { editorialHero } from '../../ui/Hero.js';

const MODAL_ADD = 'cp-activities:add';
const DAY_MS = 86_400_000;

// Audit D2 : icônes filaires (plus d'emoji UI).
const TYPE_ICONS: Record<NonNullable<CpActivity['type']>, IconName> = {
  school: 'book',
  medical: 'health',
  sport: 'ball',
  other: 'calendar',
};

interface CalendarCell {
  iso: string;
  day: number;
  isToday: boolean;
  parentCode: 'A' | 'B' | null;
  hasActivities: boolean;
}

function buildCalendar(
  today: Date,
  custody: CustodyConfig | null | undefined,
  activities: readonly CpActivity[],
): CalendarCell[] {
  const cells: CalendarCell[] = [];
  const todayString = dateToIso(today);
  for (let i = 0; i < 14; i += 1) {
    const d = new Date(today.getTime() + i * DAY_MS);
    const iso = dateToIso(d);
    cells.push({
      iso,
      day: d.getDate(),
      isToday: iso === todayString,
      parentCode: getCustodyParentCode(d, custody),
      hasActivities: activities.some((a) => a.date === iso),
    });
  }
  return cells;
}

function calendarDayTemplate(c: CalendarCell): TemplateResult {
  const cls = [
    styles.calendarDay,
    c.parentCode === 'A' ? styles.parentA : c.parentCode === 'B' ? styles.parentB : styles.unknown,
    c.isToday ? styles.today : '',
  ]
    .filter(Boolean)
    .join(' ');
  return html`<div
    class=${cls}
    data-testid="cp-cal-${c.iso}"
    data-parent=${c.parentCode ?? 'none'}
    data-today=${c.isToday ? 'true' : 'false'}
  >
    <div>${c.day.toString()}</div>
    ${c.hasActivities ? html`<div class=${styles.calendarDot}></div>` : html``}
  </div>`;
}

function calendarCardTemplate(
  cells: readonly CalendarCell[],
  custody: CustodyConfig | null,
): TemplateResult {
  return html`<div class=${styles.card} data-testid="cp-cal-card">
    <div class=${styles.cardTitle}>${t('cp_act_calendar_title')}</div>
    <div class=${styles.calendarLegend}>
      <span class=${`${styles.calendarBadge} ${styles.calendarBadgeA}`}>
        ● ${custody?.parentA ?? 'Parent A'}
      </span>
      <span class=${`${styles.calendarBadge} ${styles.calendarBadgeB}`}>
        ● ${custody?.parentB ?? 'Parent B'}
      </span>
    </div>
    <div class=${styles.calendarGrid}>${cells.map(calendarDayTemplate)}</div>
    <div class=${styles.calendarSubtitle}>${t('cp_act_next_2_weeks')}</div>
  </div>`;
}

function activityRowTemplate(a: CpActivity, homeId: string): TemplateResult {
  const icon = Icon(Icons[a.type ? TYPE_ICONS[a.type] : 'calendar'], 17);
  const isA = a.parent === 'A';
  const badgeClass = isA ? styles.cpBadgeA : styles.cpBadgeB;
  const subParts = [formatIsoShort(a.date, getLang())];
  if (a.time) subParts.push(a.time);
  if (a.location) subParts.push(a.location);
  const onRemove = (): void => {
    removeActivity(homeId, a.id);
  };
  return html`<div class=${styles.activityRow} data-testid="cp-act-row-${a.id}">
    <span class=${styles.activityIcon}>${icon}</span>
    <div class=${styles.activityMain}>
      <div class=${styles.activityTitle}>${a.title}</div>
      <div class=${styles.activitySub}>${subParts.join(' · ')}</div>
    </div>
    ${a.parent ? html`<span class=${`${styles.cpBadge} ${badgeClass}`}>${a.parent}</span>` : html``}
    <button
      type="button"
      class=${styles.removeBtn}
      data-testid="cp-act-remove-${a.id}"
      aria-label=${t('cp_act_remove')}
      @click=${onRemove}
    >
      ×
    </button>
  </div>`;
}

function listCardTemplate(activities: readonly CpActivity[], homeId: string): TemplateResult {
  const today = todayIso();
  const upcoming = [...activities]
    .filter((a) => a.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date));
  const onAddClick = (): void => {
    openModal(MODAL_ADD);
  };
  return html`<div class=${styles.card} data-testid="cp-list-card">
    <div class=${styles.cardTitle}>
      ${t('cp_act_upcoming')}
      <button
        type="button"
        class=${styles.addBtnSmall}
        data-testid="cp-act-add-btn"
        @click=${onAddClick}
      >
        ${t('cp_act_add_short')}
      </button>
    </div>
    ${upcoming.length === 0
      ? html`<div class=${styles.empty}>${t('cp_act_none_planned')}</div>`
      : upcoming.map((a) => activityRowTemplate(a, homeId))}
  </div>`;
}

interface FormState {
  title: string;
  date: string;
  time: string;
  type: NonNullable<CpActivity['type']>;
  location: string;
  parent: 'A' | 'B';
}

const formState: FormState = {
  title: '',
  date: todayIso(),
  time: '09:00',
  type: 'other',
  location: '',
  parent: 'A',
};

function modalContentTemplate(homeId: string, custody: CustodyConfig | null): TemplateResult {
  const onSubmit = (e: Event): void => {
    e.preventDefault();
    if (!formState.title.trim()) return;
    addActivity(homeId, {
      title: formState.title,
      date: formState.date,
      time: formState.time,
      location: formState.location,
      type: formState.type,
      parent: formState.parent,
    });
    // reset puis ferme
    formState.title = '';
    formState.location = '';
    closeModal();
  };
  return html`<form @submit=${onSubmit}>
    <div class=${styles.formGroup}>
      <label class=${styles.formLabel}>${t('cp_act_label_title')}</label>
      <input
        class=${styles.formInput}
        type="text"
        data-testid="cpa-title"
        placeholder=${t('cp_act_placeholder_title')}
        .value=${formState.title}
        @input=${(e: Event): void => {
          formState.title = (e.target as HTMLInputElement).value;
        }}
        required
      />
    </div>
    <div class=${styles.formGroupRow}>
      <div class=${styles.formGroup}>
        <label class=${styles.formLabel}>${t('cp_act_label_date')}</label>
        <input
          class=${styles.formInput}
          type="date"
          data-testid="cpa-date"
          .value=${formState.date}
          @input=${(e: Event): void => {
            formState.date = (e.target as HTMLInputElement).value;
          }}
        />
      </div>
      <div class=${styles.formGroup}>
        <label class=${styles.formLabel}>${t('cp_act_label_time')}</label>
        <input
          class=${styles.formInput}
          type="time"
          data-testid="cpa-time"
          .value=${formState.time}
          @input=${(e: Event): void => {
            formState.time = (e.target as HTMLInputElement).value;
          }}
        />
      </div>
    </div>
    <div class=${styles.formGroup}>
      <label class=${styles.formLabel}>${t('cp_act_label_type')}</label>
      <select
        class=${styles.formSelect}
        data-testid="cpa-type"
        .value=${formState.type}
        @change=${(e: Event): void => {
          formState.type = (e.target as HTMLSelectElement).value as FormState['type'];
        }}
      >
        <option value="school">${t('cp_act_type_school')}</option>
        <option value="medical">${t('cp_act_type_medical')}</option>
        <option value="sport">${t('cp_act_type_sport')}</option>
        <option value="other">${t('cp_act_type_other')}</option>
      </select>
    </div>
    <div class=${styles.formGroup}>
      <label class=${styles.formLabel}>${t('cp_act_label_location')}</label>
      <input
        class=${styles.formInput}
        type="text"
        data-testid="cpa-loc"
        placeholder=${t('cp_act_placeholder_location')}
        .value=${formState.location}
        @input=${(e: Event): void => {
          formState.location = (e.target as HTMLInputElement).value;
        }}
      />
    </div>
    <div class=${styles.formGroup}>
      <label class=${styles.formLabel}>${t('cp_act_label_parent')}</label>
      <select
        class=${styles.formSelect}
        data-testid="cpa-parent"
        .value=${formState.parent}
        @change=${(e: Event): void => {
          formState.parent = (e.target as HTMLSelectElement).value as 'A' | 'B';
        }}
      >
        <option value="A">${custody?.parentA ?? 'Parent A'}</option>
        <option value="B">${custody?.parentB ?? 'Parent B'}</option>
      </select>
    </div>
    <button type="submit" class=${styles.formSubmit} data-testid="cpa-submit">
      ${t('cp_act_btn_add')}
    </button>
  </form>`;
}

export function renderCpActivitiesView(): TemplateResult {
  const home = getCurrentHome();
  if (!home || home.mode !== 'coparenting') {
    return html`<section class=${styles.view} data-testid="view-cp-activities">
      <p class=${styles.empty}>${t('cp_home_no_custody')}</p>
    </section>`;
  }
  const data = home.data as CoparentingHomeData;
  const today = new Date();
  const cells = buildCalendar(today, data.custody, data.activities);
  const onAddClick = (): void => {
    openModal(MODAL_ADD);
  };

  const showModal = getModal() === MODAL_ADD;

  return html`
    <section class=${styles.view} data-testid="view-cp-activities">
      ${editorialHero({
        badge: { mode: 'coparenting', label: home.name },
        dateLine: t('cp_act_dateline'),
        title: t('cp_act_hero_title'),
        accent: t('cp_act_hero_accent'),
      })}
      ${calendarCardTemplate(cells, data.custody)} ${listCardTemplate(data.activities, home.id)}
      <button
        type="button"
        class=${styles.addBtnLarge}
        data-testid="cp-act-add-large"
        @click=${onAddClick}
      >
        ${t('cp_act_add_long')}
      </button>
      ${showModal
        ? modalTemplate({
            title: t('cp_act_modal_title'),
            content: modalContentTemplate(home.id, data.custody),
            onClose: closeModal,
            testid: 'cp-act-modal',
          })
        : html``}
    </section>
  `;
}

/** @internal pour tests : reset l'état du formulaire entre tests. */
export function _resetCpActivityFormForTests(): void {
  formState.title = '';
  formState.date = todayIso();
  formState.time = '09:00';
  formState.type = 'other';
  formState.location = '';
  formState.parent = 'A';
}
